import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import PriceExportPreviewModal from './PriceExportPreviewModal';

const PriceExportHistory = ({ orgId }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');
  const [exportsList, setExportsList] = useState([]);
  const [previewExport, setPreviewExport] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      setLoading(true);
      setError('');
      try {
        const { data, error } = await supabase
          .from('exports')
          .select('id, created_at, count, user_id, type, user_profiles(full_name, email)')
          .eq('org_id', orgId)
          .eq('type', 'epos_update')
          .order('created_at', { ascending: false });
        if (error) throw error;
        setExportsList(data || []);
      } catch (e) {
        setError(e?.message || 'Failed to load export history');
        setExportsList([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  const handleDownload = async (exportRow) => {
    if (!exportRow?.id || !orgId) return;
    setDownloading(exportRow.id);
    setError('');
    try {
      const { data, error } = await supabase
        .from('epos_price_history')
        .select('id, sku, old_price, new_price, changed_at, epos_products(name), parsed_invoices(vendor_name)')
        .eq('org_id', orgId)
        .eq('export_id', exportRow.id)
        .order('changed_at', { ascending: true });
      if (error) throw error;
      if (!data || data.length === 0) {
        setError('No price updates found for this export.');
        return;
      }

      const exportDate = exportRow?.created_at || new Date().toISOString();
      downloadCsv(data, exportDate);
    } catch (e) {
      setError(e?.message || 'Failed to download export');
    } finally {
      setDownloading('');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Export History</h2>
          <p className="text-xs text-muted-foreground">Re-download previous EPOS update batches.</p>
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}
      {loading ? (
        <div className="text-sm">Loading...</div>
      ) : exportsList.length === 0 ? (
        <div className="text-sm text-muted-foreground">No price export batches yet.</div>
      ) : (
        <div className="overflow-auto border border-border rounded-md">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-muted text-muted-foreground">
                <Th>Export Date</Th>
                <Th>User</Th>
                <Th className="text-right">Item Count</Th>
                <Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {exportsList.map((row) => {
                const userLabel = row?.user_profiles?.full_name || row?.user_profiles?.email || row?.user_id || '';
                return (
                  <tr key={row.id} className="border-t border-border">
                    <Td>{fmtDateTime(row?.created_at)}</Td>
                    <Td>{userLabel}</Td>
                    <Td num>{row?.count || 0}</Td>
                    <Td num>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewExport(row)}
                          iconName="Eye"
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(row)}
                          iconName="Download"
                          loading={downloading === row.id}
                        >
                          Re-download
                        </Button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PriceExportPreviewModal
        isOpen={Boolean(previewExport?.id)}
        onClose={() => setPreviewExport(null)}
        orgId={orgId}
        exportRow={previewExport}
      />
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 border-b border-border sticky top-0 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, num = false, className = '' }) => (
  <td className={`px-3 py-2 align-top ${mono ? 'font-mono text-[11px]' : ''} ${num ? 'text-right' : ''} ${className}`}>{children}</td>
);

function fmtDateTime(iso) { try { const d = new Date(iso); if (Number.isNaN(d.getTime())) return iso || ''; return d.toLocaleString('en-GB'); } catch { return iso || ''; } }
function toNum(v) { if (v === undefined || v === null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function formatCsvNumber(v) { if (v == null || Number.isNaN(Number(v))) return ''; return Number(v).toFixed(2); }
function csvEscape(value) { const s = value == null ? '' : String(value); return `"${s.replace(/"/g, '""')}"`; }

function downloadCsv(rows, exportDateIso) {
  const headers = ['SKU', 'Product Name', 'Old Cost', 'New Cost', 'Variance', 'Vendor', 'Export Date'];
  const csvRows = [headers.join(',')];
  const exportDate = formatCsvDate(exportDateIso);

  rows.forEach((row) => {
    const oldP = toNum(row?.old_price);
    const newP = toNum(row?.new_price);
    const variance = (oldP != null && newP != null) ? (newP - oldP) : null;
    csvRows.push([
      csvEscape(row?.sku),
      csvEscape(row?.epos_products?.name || ''),
      formatCsvNumber(oldP),
      formatCsvNumber(newP),
      formatCsvNumber(variance),
      csvEscape(row?.parsed_invoices?.vendor_name || ''),
      csvEscape(exportDate)
    ].join(','));
  });

  const csvString = csvRows.join('\r\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `epos_price_updates_${exportDate}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function formatCsvDate(iso) { try { const d = new Date(iso); if (Number.isNaN(d.getTime())) return ''; return d.toISOString().slice(0, 10); } catch { return ''; } }

export default PriceExportHistory;
